import { create } from "zustand";
import type { ReactNode } from "react";

export interface ConfirmDialogOptions {
  title: ReactNode;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /**
   * Pinta el botón de confirmar en rojo (peligro) · atajo para
   * `confirmColor: "peligro"`, se mantiene por compatibilidad con la
   * llamada de Etapa 2 (`kitchen-sink.tsx`).
   */
  danger?: boolean;
  /**
   * Color del botón de confirmar · `"peligro"` (eliminar, cancelar, dar de
   * baja definitiva) o `"advertencia"` (dar de baja REVERSIBLE — OC/CA,
   * indicadores, se puede reactivar después). Reemplaza los 9 usos de
   * `modals.openConfirmModal({confirmProps:{color:"red"|"orange"}})`.
   */
  confirmColor?: "peligro" | "advertencia";
  /** Ícono opcional en el botón de confirmar (ej. `<IconBan/>`, `<IconArchive/>`). */
  icon?: ReactNode;
}

interface ConfirmDialogState {
  opened: boolean;
  options: ConfirmDialogOptions | null;
  resolve: ((value: boolean) => void) | null;
  request: (options: ConfirmDialogOptions) => Promise<boolean>;
  respond: (value: boolean) => void;
}

/**
 * Store (sin persist) detrás de `useConfirmDialog()` · reemplaza
 * `openConfirmModal` de Mantine. El estado vive acá (no en el
 * componente) para poder resolverse desde cualquier parte de la app con
 * una sola llamada async, igual que el imperativo de Mantine.
 */
export const useConfirmDialogStore = create<ConfirmDialogState>((set, get) => ({
  opened: false,
  options: null,
  resolve: null,
  request: (options) =>
    new Promise<boolean>((resolve) => {
      // Si ya había un diálogo pendiente sin resolver (no debería pasar en
      // uso normal · un solo ConfirmDialogHost a la vez), lo resolvemos en
      // falso para no dejar una promesa colgada.
      get().resolve?.(false);
      set({ opened: true, options, resolve });
    }),
  respond: (value) => {
    get().resolve?.(value);
    set({ opened: false, resolve: null });
  },
}));

/** Hook imperativo · `const confirm = useConfirmDialog(); const ok = await confirm({...})`. */
export function useConfirmDialog() {
  return useConfirmDialogStore((s) => s.request);
}
